// src/cockpit4/workspaces/GeoDSOWorkspace.tsx
// ============================================
// GEO+DSO 一级工作区
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Radar, Search, Package, Fingerprint,
  BookOpen, MessageSquareText, Palette,
  Pickaxe, ScanLine, ListTodo, BookmarkCheck, Workflow,
} from "lucide-react";
import GEODSOEntry from "../components/GEODSOEntry";
import BrandMonitorsHub from "../components/BrandMonitorsHub";
import RealTimeSearchPanel from "../components/RealTimeSearchPanel";
import ProductDiagnosisPanel from "../components/ProductDiagnosisPanel";
import BrandDiagnosisPanel from "../components/BrandDiagnosisPanel";
import AICiteSourcePanel from "../components/AICiteSourcePanel";
import AIChatLogsPanel from "../components/AIChatLogsPanel";
import WorkCiteTrackerPanel from "../components/WorkCiteTrackerPanel";
import AIQuestionMinerPanel from "../components/AIQuestionMinerPanel";
import WebsiteGEODiagnosisPanel from "../components/WebsiteGEODiagnosisPanel";
import BrandMonitorPlansPanel from "../components/BrandMonitorPlansPanel";
import AIQuestionLibraryPanel from "../components/AIQuestionLibraryPanel";
import GeoAcquisitionLoopPanel from "../components/GeoAcquisitionLoopPanel";
import AddBrandWizardModal, { DEFAULT_SPEC, type BrandMonitorSpec } from "../components/AddBrandWizardModal";
import { GEODiagnosticRuntime } from "../../runtime/GrowthFactory/GEODiagnosticRuntime";

type DiagTab =
  | "console"
  | "realtime"
  | "product"
  | "brand"
  | "monitor"
  | "cite"
  | "chatlogs"
  | "works"
  | "miner"          // 新增：AI问题挖掘
  | "geodiag"        // 新增：官网GEO诊断
  | "plans"          // 新增：品牌监测计划
  | "qlib"           // 新增：AI问题库
  | "loop";          // 新增：GEO获客闭环

const DEFAULT_DATE_RANGE = () => {
  const today = new Date();
  const start = new Date(today.getTime() - 7 * 24 * 3600 * 1000);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const d2 = new Date();
  const d1 = new Date(d2.getTime() - 6 * 24 * 3600 * 1000);
  return { s: fmt(d1), e: fmt(d2) };
};

export default function GeoDSOWorkspace() {
  const [tab, setTab] = useState<DiagTab>("console");
  const geoDiagRuntime = useMemo(() => new GEODiagnosticRuntime(), []);
  const defDate = DEFAULT_DATE_RANGE();
  const [dateStart, setDateStart] = useState(defDate.s);
  const [dateEnd, setDateEnd] = useState(defDate.e);
  const [wizardOpen, setWizardOpen] = useState(false);

  const onDateChange = (s: string, e: string) => { setDateStart(s); setDateEnd(e); };
  const onAddBrand = () => setWizardOpen(true);

  const tabBtn = (key: DiagTab, icon: React.ReactNode, label: string, activeColor: string = "text-cyan-400") => (
    <button
      onClick={() => setTab(key)}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium transition-all whitespace-nowrap ${
        tab === key ? "text-white bg-white/[0.05]" : "text-gray-400 hover:text-gray-200 hover:bg-white/[0.03]"
      }`}
    >
      <span className={tab === key ? activeColor : ""}>{icon}</span>
      {label}
    </button>
  );

  const handleSubmitBrand = async (spec: BrandMonitorSpec) => {
    // 真实持久化：写入后端品牌监测计划（Obsidian Vault），无模拟数据
    try {
      const r = await fetch("/ai-visibility/brands", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          brandName: spec.brandName,
          websiteUrl: spec.websiteUrl,
          intro: spec.intro,
          brandWords: spec.brandWords,
          competitors: spec.competitors,
          tags: spec.tags,
          questions: spec.questions,
          frequency: spec.frequency,
          runAt: spec.runAt,
          platforms: spec.platforms,
          enabled: true,
        }),
      });
      const d = await r.json();
      setWizardOpen(false);
      if (d?.success) {
        window.alert?.(`✅ 已创建品牌「${spec.brandName}」监测任务\n频率：${spec.frequency} · ${spec.runAt}\n平台数：${spec.platforms.length}\n问题数：${spec.questions.length}`);
      } else {
        window.alert?.(`❌ 创建失败：${d?.error || "未知错误"}`);
      }
    } catch {
      setWizardOpen(false);
      window.alert?.("❌ 无法连接服务器，品牌监测计划未保存");
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-[#0a0d18]">
      {/* 子页签导航 */}
      <div className="flex items-center gap-1 px-4 pt-2 pb-1 border-b border-white/[0.06] bg-white/[0.02] overflow-x-auto shrink-0">
        {tabBtn("console", <LayoutDashboard size={13} />, "GEO+DSO 总控台", "text-cyan-400")}
        {tabBtn("realtime", <Search size={13} />, "实时搜索验证", "text-cyan-400")}
        {tabBtn("product", <Package size={13} />, "产品诊断", "text-violet-400")}
        {tabBtn("brand", <Fingerprint size={13} />, "品牌诊断", "text-emerald-400")}
        {tabBtn("monitor", <Radar size={13} />, "品牌监测中心", "text-violet-400")}
        {tabBtn("cite", <BookOpen size={13} />, "AI引用来源", "text-indigo-400")}
        {tabBtn("chatlogs", <MessageSquareText size={13} />, "AI对话记录", "text-cyan-300")}
        {tabBtn("works", <Palette size={13} />, "作品引用追踪", "text-fuchsia-400")}
        {tabBtn("miner", <Pickaxe size={13} />, "AI问题挖掘", "text-amber-300")}
        {tabBtn("geodiag", <ScanLine size={13} />, "官网GEO诊断", "text-violet-300")}
        {tabBtn("plans", <ListTodo size={13} />, "品牌监测计划", "text-indigo-300")}
        {tabBtn("qlib", <BookmarkCheck size={13} />, "AI问题库", "text-emerald-300")}
        {tabBtn("loop", <Workflow size={13} />, "GEO获客闭环", "text-fuchsia-400")}
      </div>

      {/* 内容区 */}
      <motion.div
        key={tab}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="flex-1 min-h-0 overflow-hidden"
      >
        <div className="h-full overflow-y-auto">
          {tab === "console" && <GEODSOEntry />}
          {tab === "realtime" && <RealTimeSearchPanel />}
          {tab === "product" && <ProductDiagnosisPanel />}
          {tab === "brand" && <BrandDiagnosisPanel />}
          {tab === "monitor" && (
            <BrandMonitorsHub
              runtime={geoDiagRuntime}
              onAddBrand={onAddBrand}
            />
          )}
          {tab === "cite" && (
            <AICiteSourcePanel
              dateStart={dateStart} dateEnd={dateEnd}
              onDateChange={onDateChange} onAddBrand={onAddBrand}
            />
          )}
          {tab === "chatlogs" && (
            <AIChatLogsPanel
              dateStart={dateStart} dateEnd={dateEnd}
              onDateChange={onDateChange} onAddBrand={onAddBrand}
            />
          )}
          {tab === "works" && (
            <WorkCiteTrackerPanel
              dateStart={dateStart} dateEnd={dateEnd}
              onDateChange={onDateChange} onAddBrand={onAddBrand}
            />
          )}
          {tab === "miner" && (
            <AIQuestionMinerPanel
              onAddToMonitor={onAddBrand}
              onOpenMonitorPlan={() => setTab("plans")}
            />
          )}
          {tab === "geodiag" && (
            <WebsiteGEODiagnosisPanel onOpenMiner={() => setTab("miner")} />
          )}
          {tab === "plans" && (
            <BrandMonitorPlansPanel onAddBrand={onAddBrand} />
          )}
          {tab === "qlib" && (
            <AIQuestionLibraryPanel onAddToMonitor={onAddBrand} />
          )}
          {tab === "loop" && <GeoAcquisitionLoopPanel />}
        </div>
      </motion.div>

      {/* 全域 4 步向导弹窗 */}
      <AddBrandWizardModal
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSubmit={handleSubmitBrand}
        initial={DEFAULT_SPEC()}
      />
    </div>
  );
}
